// EditableComboSelect.jsx — a select-only editable combobox (type-in-the-control).
// The trigger IS the text field: click to open, type to filter live, no separate
// filter box. Built for the PID commodity-card "Add style / size / commodity" pickers
// (PID tables UX plan, M7 / decisions 12–16) but written as a reusable bound-value
// picker primitive so other long-list dropdowns can adopt it later.
//
//  - starts-with match (case-insensitive) on the option label — decision 13
//  - close on select; reopen to add another — decision 14
//  - adaptive width: sized to the longest option, capped responsively — decision 15
//  - select-only: text matching no option is discarded on close — decision 16
//  - WAI-ARIA combobox: role=combobox input + portaled role=listbox, focus stays in
//    the input, highlighted option tracked via aria-activedescendant
//  - menu placement reuses the existing usePortalPopover hook (v1.04.09) — no clipping
//    inside modal scroll containers; menu sits at z-index 5000 above the PID modal
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import usePortalPopover from './usePortalPopover';
import { ComboMenu, ComboList, ComboOption } from '../styles/CustomerSpecsIndexStyles';

// Compact chip-row control language — matches the retired native AddSelect surface
// (--white-a66 / --cream-500 / 8px radius) with the app-wide single green focus.
const Wrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 0;
`;

const Input = styled.input`
  font: inherit;
  font-size: 0.78rem;
  width: 100%;
  padding: 4px 24px 4px 8px; /* right pad clears the caret */
  box-sizing: border-box;
  color: var(--color-text);
  background: var(--white-a66);
  border: 1px solid var(--cream-500);
  border-radius: 8px;
  text-overflow: ellipsis;

  &::placeholder { color: var(--ink-a60); }
  &:focus {
    outline: none;
    border-color: var(--green-800);
    box-shadow: 0 0 0 1px var(--green-800);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Caret = styled.span`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0.6;
  font-size: 0.7rem;
  line-height: 0;
`;

function startsWithFilter(options, query) {
  const q = String(query || '').trim().toLowerCase();
  const arr = Array.isArray(options) ? options : [];
  if (!q) return arr;
  return arr.filter((o) => String(o.label ?? '').toLowerCase().startsWith(q));
}

export default function EditableComboSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  ariaLabel,
}) {
  const { open, setOpen, anchorRef, menuRef, menuStyle } = usePortalPopover();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listId = useMemo(() => `ecs-${Math.random().toString(36).slice(2)}`, []);

  const selected = useMemo(() => {
    const v = String(value ?? '');
    return (options || []).find((o) => String(o.value) === v) || null;
  }, [options, value]);

  const filtered = useMemo(() => startsWithFilter(options, query), [options, query]);

  // Adaptive width: size to the longest option label (or placeholder), plus room for
  // padding + caret; floor so short pools still read as a control, cap so a long value
  // never blows out the row. min(…, 100%) keeps it inside a narrow card on mobile.
  const widthCh = useMemo(() => {
    const longest = (options || []).reduce(
      (m, o) => Math.max(m, String(o.label ?? '').length),
      String(placeholder).length
    );
    return Math.min(Math.max(longest + 1, 10), 34);
  }, [options, placeholder]);
  const wrapStyle = { width: `min(calc(${widthCh}ch + 2.2rem), 100%)` };

  // Clamp the highlight when filtering shrinks the list.
  useEffect(() => {
    setActiveIndex((i) => Math.max(0, Math.min(i, Math.max(0, filtered.length - 1))));
  }, [filtered.length]);

  const openMenu = () => {
    if (disabled) return;
    setQuery('');
    setActiveIndex(0);
    setOpen(true);
  };

  const pick = (opt) => {
    if (!opt) return;
    onChange?.(opt.value);
    setQuery('');
    setOpen(false);
  };

  const displayValue = open ? query : selected ? selected.label : '';

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { openMenu(); return; }
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (open) {
        e.preventDefault();
        pick(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation(); // don't let a parent modal also close on Escape
        setQuery('');
        setOpen(false);
      }
    } else if (e.key === 'Tab') {
      // Select-only: leaving the field discards any unmatched typed text.
      setQuery('');
      setOpen(false);
    }
  };

  const menu = open && !disabled ? (
    <ComboMenu
      ref={menuRef}
      role="listbox"
      id={listId}
      style={{ ...menuStyle, overflowY: 'auto' }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ComboList>
        {filtered.length === 0 ? (
          <div style={{ padding: '8px 10px', color: 'var(--shadow-a55)' }}>No matches</div>
        ) : (
          filtered.map((opt, idx) => (
            <ComboOption
              key={String(opt.value)}
              id={`${listId}-opt-${idx}`}
              type="button"
              role="option"
              aria-selected={String(opt.value) === String(value ?? '')}
              $active={idx === activeIndex}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => e.preventDefault()} /* keep focus in the input */
              onClick={() => pick(opt)}
            >
              {opt.label}
            </ComboOption>
          ))
        )}
      </ComboList>
    </ComboMenu>
  ) : null;

  return (
    <Wrap ref={anchorRef} style={wrapStyle}>
      <Input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[activeIndex] ? `${listId}-opt-${activeIndex}` : undefined}
        aria-label={ariaLabel || placeholder}
        placeholder={placeholder}
        value={displayValue}
        disabled={disabled}
        onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); if (!open) setOpen(true); }}
        onKeyDown={handleKeyDown}
        onClick={() => { if (!open) openMenu(); }}
      />
      <Caret aria-hidden="true">▾</Caret>
      {menu ? createPortal(menu, document.body) : null}
    </Wrap>
  );
}
